import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from 'react-modal';
import { useFamily } from '../../../context/FamilyContext';
import MyButton from '../../basic/MyButton';
import MyTextInput from '../../basic/MyTextInput';
import MyDropdownWeekdays from '../../basic/MyDropdownWeekdays';


Modal.setAppElement('#root');

function ModalEditAllowance({ isOpen, onClose, childId, notifySuccess, onSaveSuccess }) {
    const { allowanceData, updateAllowanceData, fetchAllowanceData } = useFamily();
    const [allowanceSettings, setAllowanceSettings] = useState({
        amount: '',
        day_of_distribution: 'Monday',
    });
    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [isFormDirty, setIsFormDirty] = useState(false);
    const hasFetchedRef = useRef(false);

    const loadAllowanceData = useCallback(() => {
        if (childId && allowanceData[childId]) {
            setAllowanceSettings({
                amount: allowanceData[childId].amount || '',
                day_of_distribution: allowanceData[childId].day_of_distribution || 'Monday',
            });
            setIsFormDirty(false);
        }
    }, [childId, allowanceData]);

    useEffect(() => {
        if (isOpen && childId && !hasFetchedRef.current) {
            setIsFetching(true);
            fetchAllowanceData(childId)
                .then(() => {
                    loadAllowanceData();
                    setError('');
                    hasFetchedRef.current = true;
                })
                .catch(err => {
                    console.error('Error fetching allowance settings:', err);
                    setError('Failed to load allowance settings.');
                })
                .finally(() => setIsFetching(false));
        }
    }, [isOpen, childId, fetchAllowanceData, loadAllowanceData]);

    useEffect(() => {
        if (!isOpen) {
            hasFetchedRef.current = false;
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setAllowanceSettings(prev => ({
            ...prev,
            [name]: value
        }));
        setIsFormDirty(true);
    };

    const handleDayChange = (e) => {
        setAllowanceSettings(prev => ({
            ...prev,
            day_of_distribution: e.target.value
        }));
        setIsFormDirty(true);
    };

    const isFormChanged = () => {
        const currentSettings = allowanceData[childId] || {};
        return allowanceSettings.amount !== currentSettings.amount ||
               allowanceSettings.day_of_distribution !== currentSettings.day_of_distribution;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!isFormChanged()) {
            onClose();
            return;
        }

        setIsSaving(true);
        setError('');
        try {
            await updateAllowanceData(childId, {
                ...allowanceSettings,
                frequency: 'Weekly'
            });
            const successMessage = 'Allowance updated successfully!';
            if (notifySuccess) {
                notifySuccess(successMessage);
            }
            if (onSaveSuccess) {
                onSaveSuccess(successMessage);
            }
            onClose();
        } catch (error) {
            console.error('Error updating allowance settings:', error);
            setError('Failed to update allowance settings.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Edit Allowance"
            style={{
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                },
                content: {
                    top: '50%',
                    left: '50%',
                    right: 'auto',
                    bottom: 'auto',
                    marginRight: '-50%',
                    transform: 'translate(-50%, -50%)'
                }
            }}
        >
            <h2>Edit Allowance</h2>
            {error && <div style={{ color: 'red' }}>{error}</div>}
            <form onSubmit={handleSave}>
                <MyTextInput
                    label="Allowance Amount"
                    name="amount"
                    value={allowanceSettings.amount}
                    onChange={handleInputChange}
                    type="number"
                    disabled={isFetching || isSaving}
                />
                
                <MyDropdownWeekdays
                    label="Deposit Day"
                    selectedDay={allowanceSettings.day_of_distribution}
                    onChange={handleDayChange}
                    error={null}
                    disabled={isFetching || isSaving}
                />

                <MyButton 
                    text={isSaving ? 'Saving...' : 'Save Changes'} 
                    onClick={handleSave} 
                    disabled={!isFormChanged() || isFetching || isSaving}
                />
                <MyButton 
                    text="Cancel" 
                    onClick={onClose} 
                    backgroundColor="white"
                    color="#dc3545"
                    hoverBackgroundColor="#f8f8f8"
                    hoverColor="#c82333"
                    disabled={isFetching || isSaving}
                />
            </form>
        </Modal>
    );
}

export default ModalEditAllowance;